import { Entity } from '@inter-ikea-core/core';
import { ProductDesign } from '@inter-ikea-storageone/range';
import { useCore } from 'contexts/core';
import { usePlannerConfig } from 'contexts/plannerConfig';
import { useAppDispatch } from 'ducks/hooks';
import {
  filterApplied,
  getFilterState,
  getMarketProducts,
  getProductsForGroup,
  getSelectedGroupId
} from 'ducks/products';
import * as R from 'ramda';
import {
  isEntitiesReplaceable,
  isProductOfEquivalentColor
} from 'rangeConfig/shared/utils/colorUtils/utils';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { default as ImageColorPicker } from './ColorPicker';
import { useBehaviorTracker } from 'contexts/behaviors';
import ColorPicker from 'components/ProductSidebar/menus/Widgets/ChangeColor/ColorPicker';
import styled from 'styled-components';
import { isMobile } from 'utils/platform';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums';
import { useIntl } from 'react-intl';
import Title from 'ui/Typography/Title';

const Wrapper = styled.div`
  width: 100%;
`;

export enum ColorPickerType {
  IMAGE,
  ICON
}

type ChangeColorProps = {
  onChangeColor: (design: ProductDesign) => void;
  entities: Entity[];
  defaultColor: ProductDesign;
  allColors: {
    productDesign: ProductDesign;
    image: string;
  }[];
  colorPickerType: ColorPickerType;
};

const ChangeColor = ({
  onChangeColor,
  entities,
  defaultColor,
  allColors,
  colorPickerType
}: ChangeColorProps) => {
  const coreApi = useCore();
  const dispatch = useAppDispatch();
  const behaviorTracker = useBehaviorTracker();
  const products = useSelector(getMarketProducts);
  const selectedGroupId = useSelector(getSelectedGroupId);
  const productsInGroup = useSelector(getProductsForGroup);
  const filterState = useSelector(getFilterState);
  const colorFilterState = filterState(selectedGroupId, 'design');
  const { formatMessage } = useIntl();

  const {
    plannerConfig: { productGroups }
  } = usePlannerConfig();

  const [currentColor, setCurrentColor] = useState(defaultColor);
  const selectedGroup = R.find(R.propEq('id', selectedGroupId), productGroups);

  const onColorChanged = useCallback(
    (design: ProductDesign, listIndex: number) => {
      const meta = { color: design, listIndex };
      behaviorTracker.withMeta(meta).custom(async () => {
        if (isEntitiesReplaceable(design, entities, products)) {
          await coreApi.performLongCoreOperation(() => {
            coreApi.validations['WallVisibilityValidation']?.disable();
            coreApi.validations['SeeThroughFramesValidation']?.disable();
            onChangeColor(design);
            setCurrentColor(design);
            coreApi.validations['WallVisibilityValidation']?.enable();
            coreApi.validations['SeeThroughFramesValidation']?.enable();
          });
        } else {
          setCurrentColor(design);
        }
      });
      filterProducts(design);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [behaviorTracker, coreApi, entities, onChangeColor, products]
  );

  const filterProducts = useCallback(
    (design: ProductDesign) => {
      if (selectedGroup) {
        if (currentColor !== design) {
          setCurrentColor(design);
        }
        if (colorFilterState.design !== design) {
          const newFilterState = { ...colorFilterState, design };
          const filteredProducts = productsInGroup
            .filter(p => isProductOfEquivalentColor(p, design))
            .map(p => p.coreProductId);
          dispatch(
            filterApplied({
              id: 'design',
              productGroupId: selectedGroup.id,
              productIdList: filteredProducts,
              filterState: newFilterState
            })
          );
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentColor, dispatch, productsInGroup, selectedGroup, selectedGroupId]
  );

  useEffect(() => {
    filterProducts(defaultColor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultColor]);

  return (
    <Wrapper style={{ margin: isMobile() ? '1.5rem' : 0 }}>
      {!isMobile() && (
        <Title type={ThemeFontStyleTypeEnum.headingSmall}>
          {formatMessage({ id: 'storageone.selectedProductChangeStyle' })}
        </Title>
      )}
      {colorPickerType === ColorPickerType.IMAGE ? (
        <>
          <ImageColorPicker
            currentColor={currentColor}
            onChangeColor={onColorChanged}
            allColors={allColors}
          />
        </>
      ) : (
        <ColorPicker
          currentColor={currentColor}
          onChangeColor={onColorChanged}
          colors={allColors.map(c => c.productDesign)}
        />
      )}
    </Wrapper>
  );
};

export default ChangeColor;
